/**
 * Cactus Corn Cares — attribution capture
 *
 * Captures fundraiser tracking data from URL parameters when a supporter
 * arrives at a Shopify product page, persists it across the shopping
 * session, and ensures it lands on the resulting order as cart attributes
 * (Shopify's "Additional details" on the order record).
 *
 * Attribute schema (must match the cart-permalink flow already in use):
 *   bundle_id, campaign_id, org_slug, tracking_code
 *
 * URL parameter names are identical to the attribute names, so a Lovable
 * fundraiser page links here as:
 *   /products/family-pack
 *     ?bundle_id=UUID
 *     &campaign_id=UUID
 *     &org_slug=northgate-s-robotics-team
 *     &tracking_code=northgate-s-robotics-team-msueg5
 *
 * Attribution model: last-touch (a new set of URL params overwrites any
 * previous attribution). Attribution older than 14 days is discarded.
 *
 * The snippet is deliberately dependency-free and touches only the public
 * Shopify cart APIs (/cart.js, /cart/update.js). No admin API access, no
 * theme Liquid required beyond a single <script> tag include.
 */

(function () {
  'use strict';

  var CONFIG = {
    storageKey: 'cc_cares_attribution',
    expiryDays: 14,
    fields: ['bundle_id', 'campaign_id', 'org_slug', 'tracking_code'],
    debug: false, // Set to true to see console logs while testing
  };

  function log() {
    if (!CONFIG.debug) return;
    var args = ['[cc-cares]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  // ---------------------------------------------------------------
  // Attribution I/O
  // ---------------------------------------------------------------

  function readFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var attribution = {};
    var hasAny = false;
    for (var i = 0; i < CONFIG.fields.length; i++) {
      var field = CONFIG.fields[i];
      var val = params.get(field);
      if (val) {
        attribution[field] = val;
        hasAny = true;
      }
    }
    return hasAny ? attribution : null;
  }

  function readFromStorage() {
    try {
      var raw = localStorage.getItem(CONFIG.storageKey);
      if (!raw) return null;
      var data = JSON.parse(raw);
      var ageMs = Date.now() - data.capturedAt;
      var maxAgeMs = CONFIG.expiryDays * 24 * 60 * 60 * 1000;
      if (ageMs > maxAgeMs) {
        log('attribution expired, clearing');
        localStorage.removeItem(CONFIG.storageKey);
        return null;
      }
      return data.attribution;
    } catch (e) {
      log('storage read failed', e);
      return null;
    }
  }

  function saveToStorage(attribution) {
    try {
      localStorage.setItem(
        CONFIG.storageKey,
        JSON.stringify({
          attribution: attribution,
          capturedAt: Date.now(),
        })
      );
      log('saved to storage', attribution);
    } catch (e) {
      log('storage write failed', e);
    }
  }

  // ---------------------------------------------------------------
  // Shopify cart interaction
  // ---------------------------------------------------------------

  function getCurrentCartAttributes() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        return cart.attributes || {};
      });
  }

  function writeCartAttributes(attribution) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attribution }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart update failed: ' + r.status);
        log('wrote cart attributes', attribution);
        return r.json();
      })
      .catch(function (e) {
        log('cart update failed', e);
      });
  }

  function ensureCartHasAttribution(attribution) {
    if (!attribution) return Promise.resolve();
    return getCurrentCartAttributes()
      .then(function (current) {
        var needsUpdate = false;
        for (var i = 0; i < CONFIG.fields.length; i++) {
          var field = CONFIG.fields[i];
          if (attribution[field] && current[field] !== attribution[field]) {
            needsUpdate = true;
            break;
          }
        }
        if (needsUpdate) {
          log('cart attributes drifted, rewriting');
          return writeCartAttributes(attribution);
        }
        log('cart attributes already correct, no write needed');
      })
      .catch(function (e) {
        log('ensure failed', e);
      });
  }

  // ---------------------------------------------------------------
  // Add-to-cart interception
  //
  // Themes add to cart via three paths, all of which we need to catch:
  //   1. fetch('/cart/add.js', ...) — modern Ajax themes
  //   2. XMLHttpRequest to /cart/add — older Ajax themes
  //   3. <form action="/cart/add"> submission — non-Ajax themes
  //
  // Cases 1 and 2: after the add completes, re-verify attribution.
  //   (Shopify normally preserves cart attributes across adds, but if the
  //   cart was cleared and recreated, the attributes are lost. We rewrite.)
  //
  // Case 3: the form submit navigates away; the next page load runs init()
  //   which re-applies attribution automatically. Nothing extra to do.
  // ---------------------------------------------------------------

  function hookCartAdd(attribution) {
    // Patch fetch
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var urlArg = args[0];
      var urlStr =
        typeof urlArg === 'string' ? urlArg : (urlArg && urlArg.url) || '';
      var isCartAdd = urlStr.indexOf('/cart/add') !== -1;
      var result = origFetch.apply(this, args);
      if (isCartAdd) {
        result.then(function () {
          log('cart/add via fetch — re-verifying attribution');
          ensureCartHasAttribution(attribution);
        });
      }
      return result;
    };

    // Patch XMLHttpRequest
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ccCaresUrl = url;
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__ccCaresUrl && this.__ccCaresUrl.indexOf('/cart/add') !== -1) {
        this.addEventListener('load', function () {
          log('cart/add via XHR — re-verifying attribution');
          ensureCartHasAttribution(attribution);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  // ---------------------------------------------------------------
  // Entry point
  // ---------------------------------------------------------------

  function init() {
    var attribution = readFromUrl();

    if (attribution) {
      log('captured from URL', attribution);
      saveToStorage(attribution);
      writeCartAttributes(attribution);
    } else {
      attribution = readFromStorage();
      if (attribution) {
        log('loaded from storage', attribution);
        ensureCartHasAttribution(attribution);
      }
    }

    if (attribution) {
      // Expose for debugging and integration
      window.__ccCares = {
        attribution: attribution,
        rewrite: function () {
          return writeCartAttributes(attribution);
        },
        inspect: function () {
          return getCurrentCartAttributes();
        },
        clear: function () {
          localStorage.removeItem(CONFIG.storageKey);
          log('storage cleared');
        },
      };
      document.documentElement.setAttribute(
        'data-cc-cares',
        attribution.tracking_code || 'active'
      );
      hookCartAdd(attribution);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();